import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getInvoice } from "../../core/api/invoiceService";
import { getPatient } from "../../core/api/patientService";
import { getDoctor } from "../../core/api/doctorService";

const fmt = (v) => Number(v || 0).toLocaleString("en-IN");
const formatDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`;
};

const InvoicePrint = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [patient, setPatient] = useState(null);
  const [doctorName, setDoctorName] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getInvoice(id)
      .then(async (res) => {
        if (cancelled) return;
        const inv = res.data;
        setInvoice(inv);
        const patientId = inv.patientID ?? inv.patientId;
        const doctorId = inv.doctorID ?? inv.doctorId;
        const [patientRes, doctorRes] = await Promise.all([
          patientId ? getPatient(patientId).catch(() => null) : Promise.resolve(null),
          doctorId ? getDoctor(doctorId).catch(() => null) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        if (patientRes) setPatient(patientRes.data);
        if (doctorRes) setDoctorName(doctorRes.data?.doctorName || doctorRes.data?.name || "");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <div className="page-wrapper"><div className="content"><p>Loading...</p></div></div>;
  if (!invoice) return <div className="page-wrapper"><div className="content"><p>Invoice not found.</p></div></div>;

  const rows = [
    ...(invoice.procedureLines || []).map((l) => ({
      name: l.procedureName || l.name || "Procedure",
      qty: l.qty ?? l.quantity ?? 1,
      rate: l.rate,
      amount: l.amount,
    })),
    ...(invoice.productLines || []).map((l) => ({
      name: l.itemName || l.productName || l.name || "Product",
      qty: l.qty ?? l.quantity ?? 1,
      rate: l.rate,
      amount: l.amount,
    })),
  ];

  return (
    <div className="page-wrapper invoice-print-page">
      <style>{`
        @media print {
          .header, #sidebar, .page-header, .no-print { display: none !important; }
          .page-wrapper { margin: 0 !important; padding: 0 !important; }
          .invoice-print-area { box-shadow: none !important; }
          body { background: #fff !important; }
        }
        .invoice-print-area { max-width: 820px; margin: 0 auto; background: #fff; padding: 40px; font-family: Arial, sans-serif; color: #000; }
        .invoice-print-area table { width: 100%; border-collapse: collapse; }
        .invoice-print-area th, .invoice-print-area td { border: 1px solid #000; padding: 8px 10px; }
        .invoice-print-area .patient-box { border: 1px solid #000; padding: 12px 16px; margin-bottom: 30px; }
        .invoice-print-area .patient-box .row { display: flex; justify-content: space-between; margin-bottom: 8px; }
        .invoice-print-area .patient-box .row:last-child { margin-bottom: 0; }
        .invoice-print-area .summary-table { width: 260px; margin-left: auto; margin-top: -1px; }
        .invoice-print-area .summary-table td:first-child { font-weight: 600; }
        .invoice-print-area .summary-table td:last-child { text-align: right; }
        .invoice-print-area .summary-table .grand-total td { font-weight: bold; font-size: 1.05em; }
        .invoice-print-area .signature { text-align: right; margin-top: 80px; font-weight: 600; }
      `}</style>

      <div className="content no-print d-flex justify-content-end gap-2 mb-3">
        <button
          className="btn btn-cancel"
          onClick={() => {
            // Print is always opened via window.open(..., "_blank") from the invoice
            // list/form, so this tab has no in-app history to go back to — close the
            // tab if it was opened that way, otherwise fall back to the invoice list.
            if (window.opener) window.close();
            else navigate("/invoices");
          }}
        >
          Back
        </button>
        <button className="btn btn-submit" onClick={() => window.print()}>Print</button>
      </div>

      <div className="invoice-print-area">
        <div className="patient-box">
          <div className="row">
            <span>PATIENT NAME : {patient?.patientName || invoice.patientName || "—"}</span>
            <span>AGE:{patient?.age ?? "—"}/{patient?.gender || "—"}</span>
          </div>
          <div className="row">
            <span>REGISTER NUMBER:{patient?.opNumber || "—"}</span>
            <span>DATE:{formatDate(invoice.invoiceDate)}</span>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th style={{ width: 60 }}>SL.NO</th>
              <th>TREATMENT DONE</th>
              <th style={{ width: 100 }}>QUANTITY</th>
              <th style={{ width: 100 }}>RATE</th>
              <th style={{ width: 120 }}>AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: "center" }}>No line items</td></tr>
            ) : rows.map((r, idx) => (
              <tr key={idx}>
                <td style={{ textAlign: "center" }}>{idx + 1}</td>
                <td>{r.name}</td>
                <td style={{ textAlign: "center" }}>{String(r.qty).padStart(2, "0")}</td>
                <td style={{ textAlign: "right" }}>{fmt(r.rate)}</td>
                <td style={{ textAlign: "right" }}>{fmt(r.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <table className="summary-table">
          <tbody>
            <tr className="grand-total">
              <td>Total Amount</td>
              <td>{fmt(invoice.netTotal)}</td>
            </tr>
            <tr>
              <td>Total Paid</td>
              <td>{fmt(invoice.paidAmount)}</td>
            </tr>
            <tr>
              <td>Balance</td>
              <td>{fmt(invoice.balanceAmount)}</td>
            </tr>
          </tbody>
        </table>

        {doctorName && <div className="signature"> {doctorName}</div>}
      </div>
    </div>
  );
};

export default InvoicePrint;
